import React, { useState } from "react"
import SectionCard from "../../../shared/components/SectionCard"
import StatusPill from "../../../shared/components/StatusPill"
import { ui } from "../../../shared/styles/uiSystem"
import {
  Field,
  LauncherPatientCard,
  ClinicalQrToolsCard,
} from "./ClinicalHistoryAtoms"
import {
  inputStyle,
  buttonStyle,
  primaryButtonStyle,
} from "./ClinicalHistoryStyles"

function EmptyStateBlock({ text, minHeight = 96 }) {
  return (
    <div
      style={{
        ...ui.emptyState,
        minHeight,
        background: "#ffffff",
      }}
    >
      {text}
    </div>
  )
}

function SummaryTile({ title, value, subtitle, tone = "neutral" }) {
  const visual =
    tone === "danger"
      ? { bg: "#fff7ed", border: "#fed7aa", value: "#c2410c" }
      : tone === "info"
        ? { bg: "#eff6ff", border: "#bfdbfe", value: "#1d4ed8" }
        : tone === "success"
          ? { bg: "#f0fdf4", border: "#bbf7d0", value: "#166534" }
          : tone === "warning"
            ? { bg: "#fffbeb", border: "#fde68a", value: "#b45309" }
            : { bg: "#f8fafc", border: "#e2e8f0", value: "#0f172a" }

  return (
    <div
      style={{
        borderRadius: 16,
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        padding: 12,
        display: "grid",
        gap: 4,
        alignContent: "start",
        minHeight: 84,
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 800,
          color: "#64748b",
          textTransform: "uppercase",
          letterSpacing: 0.3,
        }}
      >
        {title}
      </div>

      <div
        style={{
          fontSize: 20,
          lineHeight: 1.05,
          fontWeight: 900,
          color: visual.value,
          wordBreak: "break-word",
        }}
      >
        {value}
      </div>

      <div
        style={{
          fontSize: 12,
          color: "#64748b",
          lineHeight: 1.35,
        }}
      >
        {subtitle}
      </div>
    </div>
  )
}

function CollapsibleAdminBlock({
  title,
  subtitle,
  open,
  onToggle,
  children,
}) {
  return (
    <div
      style={{
        ...ui.card,
        borderRadius: 20,
        padding: 14,
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: "100%",
          background: "transparent",
          border: "none",
          padding: 0,
          margin: 0,
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 18,
                fontWeight: 900,
                color: "#0f172a",
                lineHeight: 1.15,
              }}
            >
              {title}
            </div>

            <div
              style={{
                fontSize: 13,
                color: "#64748b",
                marginTop: 4,
                lineHeight: 1.45,
              }}
            >
              {subtitle}
            </div>
          </div>

          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 999,
              border: "1px solid #dbeafe",
              background: "#f8fbff",
              color: "#1d4ed8",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 18,
              fontWeight: 900,
              flexShrink: 0,
            }}
          >
            {open ? "−" : "+"}
          </div>
        </div>
      </button>

      {open ? <div style={{ marginTop: 14 }}>{children}</div> : null}
    </div>
  )
}

export default function ClinicalHistoryLauncherView({
  clinicId,
  isReadOnly,
  readOnlyReason,

  launcherQuery,
  setLauncherQuery,
  launcherLoading,
  launcherPatients,

  showQuickCreate,
  setShowQuickCreate,
  quickCreateForm,
  setQuickCreateForm,
  resetQuickCreateForm,
  creatingPatient,
  onCreatePatient,
  onOpenPatientHistory,

  qrLoading,
  qrGenerating,
  qrInfo,
  qrImageUrl,
  qrImageLoading,
  onGenerateQrLink,
  onCopyQrLink,
  onOpenQrLink,
  onDownloadQr,
  onPrintQrPoster,
  onOpenUpgrade,
}) {
  const canSearch = launcherQuery.trim().length >= 2
  const [qrPanelOpen, setQrPanelOpen] = useState(false)

  return (
    <div style={{ padding: 24, background: "#f8fafc", minHeight: "100vh" }}>
      <div
        style={{
          display: "grid",
          gap: 18,
          alignContent: "start",
        }}
      >
        <div
          style={{
            ...ui.card,
            borderRadius: 22,
            padding: 16,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 420px)",
              gap: 14,
              alignItems: "end",
            }}
          >
            <div
              style={{
                display: "grid",
                gap: 8,
                alignContent: "start",
                minWidth: 0,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: "#64748b",
                }}
              >
                Módulo clínico
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 900,
                    color: "#0f172a",
                    lineHeight: 1.02,
                    letterSpacing: "-0.03em",
                    wordBreak: "break-word",
                  }}
                >
                  Historia clínica
                </div>

                <StatusPill tone={clinicId ? "success" : "warning"}>
                  {clinicId ? "Clínica activa" : "Selecciona clínica"}
                </StatusPill>

                {isReadOnly ? (
                  <StatusPill tone="danger">
                    {readOnlyReason || "Solo lectura"}
                  </StatusPill>
                ) : null}
              </div>

              <div
                style={{
                  fontSize: 13,
                  color: "#64748b",
                  lineHeight: 1.45,
                  maxWidth: 760,
                }}
              >
                Busca un paciente, créalo en el momento y entra a su historia clínica
                sin depender de Agenda o Pacientes.
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gap: 10,
                alignContent: "start",
                minWidth: 0,
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 10,
                }}
              >
                <SummaryTile
                  title="Búsqueda"
                  value={canSearch ? "Activa" : "En espera"}
                  subtitle={
                    canSearch
                      ? "Ya puedes consultar coincidencias"
                      : "Escribe al menos 2 caracteres"
                  }
                  tone={canSearch ? "info" : "neutral"}
                />

                <SummaryTile
                  title="Alta rápida"
                  value={showQuickCreate ? "Abierta" : "Oculta"}
                  subtitle={
                    showQuickCreate
                      ? "Captura inmediata disponible"
                      : "Disponible cuando la necesites"
                  }
                  tone={showQuickCreate ? "success" : "neutral"}
                />
              </div>
            </div>
          </div>
        </div>

        {!clinicId ? (
          <SectionCard
            title="Selecciona una clínica"
            subtitle="Necesitas una clínica activa para abrir el módulo clínico"
            defaultOpen={true}
          >
            <EmptyStateBlock
              minHeight={110}
              text="Para abrir historia clínica desde el menú necesitas tener una clínica activa."
            />
          </SectionCard>
        ) : (
          <>
            <SectionCard
              title="Buscar paciente"
              subtitle="Busca por nombre, teléfono o correo. No se precargan todos los pacientes."
              defaultOpen={true}
            >
              <div
                style={{
                  display: "grid",
                  gap: 14,
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0,1fr) auto",
                    gap: 12,
                    alignItems: "end",
                  }}
                >
                  <div style={{ display: "grid", gap: 6 }}>
                    <div style={ui.label}>Criterio de búsqueda</div>
                    <input
                      value={launcherQuery}
                      onChange={(e) => setLauncherQuery(e.target.value)}
                      placeholder="Escribe al menos 2 caracteres"
                      style={inputStyle}
                    />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      justifyContent: "flex-end",
                    }}
                  >
                    <button
                      onClick={() => setShowQuickCreate((prev) => !prev)}
                      disabled={isReadOnly}
                      style={{
                        ...buttonStyle,
                        cursor: isReadOnly ? "not-allowed" : "pointer",
                        opacity: isReadOnly ? 0.7 : 1,
                      }}
                    >
                      {showQuickCreate ? "Cerrar alta rápida" : "Alta rápida"}
                    </button>
                  </div>
                </div>

                {showQuickCreate ? (
                  <div
                    style={{
                      ...ui.card,
                      borderRadius: 18,
                      padding: 14,
                      background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
                    }}
                  >
                    <div style={{ display: "grid", gap: 12 }}>
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 16,
                            fontWeight: 900,
                            color: "#0f172a",
                            lineHeight: 1.2,
                          }}
                        >
                          Alta rápida de paciente
                        </div>

                        <StatusPill tone="info">Captura inmediata</StatusPill>
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, minmax(0,1fr))",
                          gap: 12,
                        }}
                      >
                        <Field label="Nombre(s)">
                          <input
                            value={quickCreateForm.firstName}
                            onChange={(e) =>
                              setQuickCreateForm((prev) => ({
                                ...prev,
                                firstName: e.target.value,
                              }))
                            }
                            style={inputStyle}
                            disabled={isReadOnly}
                          />
                        </Field>

                        <Field label="Apellido(s)">
                          <input
                            value={quickCreateForm.lastName}
                            onChange={(e) =>
                              setQuickCreateForm((prev) => ({
                                ...prev,
                                lastName: e.target.value,
                              }))
                            }
                            style={inputStyle}
                            disabled={isReadOnly}
                          />
                        </Field>

                        <Field label="Teléfono">
                          <input
                            value={quickCreateForm.phone}
                            onChange={(e) =>
                              setQuickCreateForm((prev) => ({
                                ...prev,
                                phone: e.target.value,
                              }))
                            }
                            style={inputStyle}
                            disabled={isReadOnly}
                          />
                        </Field>

                        <Field label="Correo">
                          <input
                            value={quickCreateForm.email}
                            onChange={(e) =>
                              setQuickCreateForm((prev) => ({
                                ...prev,
                                email: e.target.value,
                              }))
                            }
                            style={inputStyle}
                            disabled={isReadOnly}
                          />
                        </Field>

                        <Field label="Fecha de nacimiento">
                          <input
                            type="date"
                            value={quickCreateForm.birthDate}
                            onChange={(e) =>
                              setQuickCreateForm((prev) => ({
                                ...prev,
                                birthDate: e.target.value,
                              }))
                            }
                            style={inputStyle}
                            disabled={isReadOnly}
                          />
                        </Field>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          onClick={() => {
                            setShowQuickCreate(false)
                            resetQuickCreateForm()
                          }}
                          style={buttonStyle}
                        >
                          Cancelar
                        </button>

                        <button
                          onClick={onCreatePatient}
                          disabled={creatingPatient || isReadOnly}
                          style={{
                            ...primaryButtonStyle,
                            cursor:
                              creatingPatient || isReadOnly ? "not-allowed" : "pointer",
                            opacity: creatingPatient || isReadOnly ? 0.7 : 1,
                          }}
                        >
                          {creatingPatient
                            ? "Creando..."
                            : "Crear y abrir historia"}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}

                {!canSearch && !launcherLoading ? (
                  <EmptyStateBlock text="Escribe al menos 2 caracteres para buscar pacientes." />
                ) : null}

                {launcherLoading ? (
                  <EmptyStateBlock text="Buscando pacientes..." />
                ) : null}

                {canSearch && !launcherLoading && !launcherPatients.length ? (
                  <EmptyStateBlock text="No se encontraron pacientes con ese criterio." />
                ) : null}

                {!!launcherPatients.length ? (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                      gap: 12,
                    }}
                  >
                    {launcherPatients.map((patient) => (
                      <LauncherPatientCard
                        key={patient.id}
                        patient={patient}
                        onOpen={() => onOpenPatientHistory(patient)}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            </SectionCard>

            <CollapsibleAdminBlock
              title="Pre-registro QR (opcional)"
              subtitle="Herramienta administrativa para recepción, WhatsApp o cartel impreso. Se mantiene cerrada hasta que realmente la necesites."
              open={qrPanelOpen}
              onToggle={() => setQrPanelOpen((prev) => !prev)}
            >
              <ClinicalQrToolsCard
                qrLoading={qrLoading}
                qrGenerating={qrGenerating}
                qrInfo={qrInfo}
                qrImageUrl={qrImageUrl}
                qrImageLoading={qrImageLoading}
                isReadOnly={isReadOnly}
                readOnlyReason={readOnlyReason}
                onGenerate={onGenerateQrLink}
                onCopy={onCopyQrLink}
                onOpen={onOpenQrLink}
                onDownload={onDownloadQr}
                onPrint={onPrintQrPoster}
                onUpgrade={onOpenUpgrade}
              />
            </CollapsibleAdminBlock>
          </>
        )}
      </div>
    </div>
  )
}